import type { DeployFailureContext } from '@volley/crucible-ipc-types/ipc-channels';

export type DeployState = 'idle' | 'starting' | 'running' | 'deploying' | 'deployed' | 'error';

export type DeployActionKind = 'idle' | 'blocked' | 'deploying' | 'deployed' | 'failed';

export type DeployActionTone = 'neutral' | 'active' | 'success' | 'warning' | 'error';

export type DeployFailurePrimaryCta = 'fix-in-agent' | 'retry-deploy';
export type DeployFailureSecondaryCta = 'view-github';

export interface DeployActionState {
  kind: DeployActionKind;
  label: string;
  tone: DeployActionTone;
  badgeClassName: string;
  deployButtonLabel: string;
  deployButtonTitle: string;
  deployButtonDisabled: boolean;
  deployButtonGated: boolean;
  failurePrimaryCta?: DeployFailurePrimaryCta;
  failureSecondaryCtas: DeployFailureSecondaryCta[];
  disabledReason?: string;
  debugSource: string;
}

export interface DeployActionInput {
  deployState: DeployState;
  deployFailureContext?: DeployFailureContext;
  localFailedCheck?: string | null;
  ciTriggeredThisDeploy: boolean;
  hasAnthropicKey: boolean;
}

// The Deploy-to-TV button is intentionally NOT gated on build readiness
// (phase / status / build-order completeness). A game is deployable as
// soon as it is physically deployable — the only locks are the
// runtime-capability check (`publishBacked`, applied at the Workspace.tsx
// call site) and the in-flight `deployState === 'deploying'` lock below.
// A deploy that can't succeed (e.g. a half-built game) fails CI and
// surfaces via the `'error'` branch (DeployFailureCard + Retry), rather
// than being pre-blocked here.
export function deriveDeployActionState(input: DeployActionInput): DeployActionState {
  if (input.deployState === 'deploying') {
    return {
      kind: 'deploying',
      label: 'Deploying...',
      tone: 'active',
      badgeClassName: 'bg-yellow-500/20 text-yellow-400',
      deployButtonLabel: 'Deploying...',
      deployButtonTitle: 'Deploy in flight — please wait.',
      deployButtonDisabled: true,
      deployButtonGated: false,
      failureSecondaryCtas: [],
      disabledReason: 'Deploy in flight — please wait.',
      debugSource: 'deploy:deploying',
    };
  }

  if (input.deployState === 'error') {
    const failure =
      input.deployFailureContext?.status === 'failure' ? input.deployFailureContext : undefined;
    const failedJobs = failure?.failedJobs ?? [];
    const hasRunLink = !!(
      failure?.failedRunUrl ??
      failure?.workflowRun?.htmlUrl ??
      failedJobs[0]?.htmlUrl
    );
    const canFixInAgent = input.hasAnthropicKey && failedJobs.length > 0;
    return {
      kind: 'failed',
      label: 'Error',
      tone: 'error',
      badgeClassName: 'bg-red-500/20 text-red-400',
      deployButtonLabel: 'Retry deploy',
      deployButtonTitle: 'Retry deploy against the latest local game state.',
      deployButtonDisabled: false,
      deployButtonGated: false,
      failurePrimaryCta: canFixInAgent ? 'fix-in-agent' : 'retry-deploy',
      failureSecondaryCtas: hasRunLink ? ['view-github'] : [],
      debugSource: canFixInAgent
        ? 'deploy:error:actionable'
        : input.localFailedCheck
          ? 'deploy:error:local-check'
          : input.ciTriggeredThisDeploy
            ? 'deploy:error:ci-no-agent'
            : 'deploy:error:pre-ci',
    };
  }

  if (input.deployState === 'deployed' || input.deployState === 'running') {
    return {
      kind: 'deployed',
      label: input.deployState === 'running' ? 'Running' : 'Deployed',
      tone: 'success',
      badgeClassName: 'bg-green-500/20 text-green-400',
      deployButtonLabel: 'Deploy',
      deployButtonTitle: 'Deploy this game to the TV cluster (commits + pushes + CI).',
      deployButtonDisabled: false,
      deployButtonGated: false,
      failureSecondaryCtas: [],
      debugSource: `deploy:${input.deployState}`,
    };
  }

  if (input.deployState === 'starting') {
    return {
      kind: 'deploying',
      label: 'Starting...',
      tone: 'active',
      badgeClassName: 'bg-blue-500/20 text-blue-400',
      deployButtonLabel: 'Deploy',
      deployButtonTitle: 'Deploy this game to the TV cluster (commits + pushes + CI).',
      deployButtonDisabled: false,
      deployButtonGated: false,
      failureSecondaryCtas: [],
      debugSource: 'deploy:starting',
    };
  }

  return {
    kind: 'idle',
    label: 'Draft',
    tone: 'neutral',
    badgeClassName: 'bg-white/10 text-white/50',
    deployButtonLabel: 'Deploy',
    deployButtonTitle: 'Deploy this game to the TV cluster (commits + pushes + CI).',
    deployButtonDisabled: false,
    deployButtonGated: false,
    failureSecondaryCtas: [],
    debugSource: 'deploy:idle',
  };
}
